export const DIAGNOSTIC_SCHEMA_VERSION = 1 as const;

export const DIAGNOSTIC_LIMITS = {
  string: 240,
  longString: 600,
  label: 80,
  array: 50,
  selectionDepth: 4,
  warnings: 8,
} as const;

export type DiagnosticMoneySource =
  | 'menu_calculation'
  | 'local_cart'
  | 'local_estimate'
  | 'displayed_ui'
  | 'pricing_request'
  | 'pricing_backend'
  | 'diagnostic_comparison';

export type DiagnosticMoneyAvailability = 'available' | 'loading' | 'unavailable';

export interface DiagnosticMoneyValue {
  minorAmount?: number;
  currency?: string;
  formatted?: string;
  source: DiagnosticMoneySource;
  availability?: DiagnosticMoneyAvailability;
}

export interface DiagnosticPriceContribution {
  kind?: 'modifier' | 'picker' | 'slot' | 'child';
  name?: string;
  id?: string;
  sanityId?: string;
  plu?: string;
  quantity?: number;
  multiplier?: number;
  price?: DiagnosticMoneyValue;
}

export interface DiagnosticCaptureContext {
  capturedAt: string;
  pathname?: string;
  routeParams?: Record<string, string>;
  platform?: string;
  osVersion?: string;
  appVersion?: string;
  buildVersion?: string;
  otaUpdateId?: string;
  otaChannel?: string;
  commitId?: string;
  easBuildId?: string;
  environment?: string;
  region?: string;
  locale?: string;
  variants?: Record<string, string | boolean>;
}

export interface DiagnosticUserContext {
  authenticationState: 'anonymous' | 'authenticated_profile_loading' | 'authenticated';
  name?: string;
  email?: string;
  cognitoId?: string;
  legacyCognitoId?: string;
  loyaltyId?: string;
  loyaltyProfileLoaded?: boolean;
  loyaltyProfileId?: string;
  identitiesCorrespond?: boolean;
}

export interface DiagnosticStoreContext {
  selectionState: 'none' | 'selected';
  number?: string;
  sanityId?: string;
  name?: string;
  publicAddress?: string;
  posVendor?: string;
  serviceMode?: string;
  localTime?: string;
  activeDayparts?: string[];
  configuredDayparts?: string[];
  serviceModeHours?: string;
  daypartHours?: string;
  availability?: string;
  isOneMenu?: boolean;
}

export interface DiagnosticPdpContext {
  implementation: 'one_menu' | 'legacy';
  menuObjectType?: string;
  rootId?: string;
  selectedId?: string;
  sanityId?: string;
  name?: string;
  plu?: string;
  quantity?: number;
  step?: string;
  mode?: 'add' | 'edit';
  cartEntryId?: string;
  offerId?: string;
  rewardId?: string;
  rewardBenefitId?: string;
  baseUnitPrice?: DiagnosticMoneyValue;
  configuredUnitPrice?: DiagnosticMoneyValue;
  displayedTotal?: DiagnosticMoneyValue;
  selections?: DiagnosticPriceContribution[];
}

export interface DiagnosticCartEntry {
  type?: string;
  name?: string;
  id?: string;
  sanityId?: string;
  cartId?: string;
  plu?: string;
  quantity?: number;
  unitPrice?: DiagnosticMoneyValue;
  lineTotal?: DiagnosticMoneyValue;
  offerId?: string;
  rewardId?: string;
  rewardBenefitId?: string;
  contributions?: DiagnosticPriceContribution[];
  children?: DiagnosticCartEntry[];
}

export type DiagnosticCartSummaryKind =
  | 'item_total_sum'
  | 'subtotal'
  | 'discount'
  | 'delivery_fee'
  | 'service_fee'
  | 'small_cart_fee'
  | 'geographical_fee'
  | 'fee'
  | 'surcharge'
  | 'tax'
  | 'total';

export interface DiagnosticCartSummaryRow {
  kind: DiagnosticCartSummaryKind;
  label?: string;
  money: DiagnosticMoneyValue;
}

export interface DiagnosticCartContext {
  effectiveImplementation: 'legacy' | 'minimal';
  experimentSelectedImplementation?: 'legacy' | 'minimal';
  recoveryOverride?: boolean;
  legacyEntryCount?: number;
  minimalEntryCount?: number;
  rbiOrderId?: string;
  entries?: DiagnosticCartEntry[];
  displayedSummaryRows?: DiagnosticCartSummaryRow[];
}

export interface DiagnosticPricingInput {
  storeNumber?: string;
  serviceMode?: string;
  cartEntryCount?: number;
  requestedTotal?: DiagnosticMoneyValue;
  checkoutRepriceRequestId?: string;
  requestId?: string;
  correlationId?: string;
}

export interface DiagnosticError {
  code?: string;
  message?: string;
}

export interface DiagnosticPricingContext {
  logicalAttemptId: number;
  sourceApi: 'priceOrder' | 'priceOrderV2';
  attemptStartedAt: string;
  latestRequestAt: string;
  requestCount: number;
  retryCount: number;
  phase: 'requesting' | 'polling' | 'succeeded' | 'failed';
  input?: DiagnosticPricingInput;
  rbiOrderId?: string;
  pricingStatus?: string;
  orderStatus?: string;
  deliveryStatus?: string;
  returnedTotal?: DiagnosticMoneyValue;
  error?: DiagnosticError;
}

export interface DiagnosticPricingDiscrepancy {
  classification: 'confirmed_price_mismatch';
  surface: 'cart' | 'checkout';
  correlation: 'current_cart_request' | 'current_checkout_selection';
  observedAt: string;
  requestedSubtotal: DiagnosticMoneyValue;
  serverSubtotal: DiagnosticMoneyValue;
  difference: DiagnosticMoneyValue;
  rbiOrderId?: string;
  orderStatus?: string;
}

export interface DiagnosticContextV1 {
  schemaVersion: typeof DIAGNOSTIC_SCHEMA_VERSION;
  capture?: DiagnosticCaptureContext;
  user?: DiagnosticUserContext;
  store?: DiagnosticStoreContext;
  pdp?: DiagnosticPdpContext;
  cart?: DiagnosticCartContext;
  pricing?: DiagnosticPricingContext;
  pricingDiscrepancy?: DiagnosticPricingDiscrepancy;
  warnings?: string[];
}

export type DiagnosticContextContribution = Partial<
  Omit<DiagnosticContextV1, 'schemaVersion' | 'warnings'>
>;

export type DiagnosticContributorGetter = () => DiagnosticContextContribution | null | undefined;

export interface DiagnosticLauncherPosition {
  x: number;
  y: number;
}

export type DiagnosticClipboardResult = { ok: true } | { ok: false; reason: string };
